/**
 * Split a persisted menu item name (e.g. "Combo - Pódio Maratona (Calabresa, Marguerita)")
 * into a clean title and the list of choices inside the last parenthetical block.
 *
 * Used in "order mode" (selectable menus) so choices can be shown in a dedicated
 * "Infos" field instead of being appended to the item title.
 */

export interface SplitItemNameResult {
  /** Item title without the trailing choices block. */
  title: string;
  /** Individual choices extracted from the parenthetical block. */
  choices: string[];
}

/**
 * Splits the raw item name.
 * - The last `(...)` block is treated as the choices list.
 * - Choices are split by comma and trimmed.
 * - If no parenthetical block is found, the full name is the title and choices is empty.
 */
export const splitItemChoices = (rawName: string): SplitItemNameResult => {
  const name = String(rawName ?? "").trim();
  if (!name) {
    return { title: "", choices: [] };
  }

  const match = name.match(/^(.*)\s*\(([^)]*)\)\s*$/);
  if (!match) {
    return { title: name, choices: [] };
  }

  const title = match[1].trim();
  const choices = match[2]
    .split(",")
    .map((c) => c.trim())
    .filter((c) => c.length > 0);

  return { title, choices };
};

/**
 * Collects all choices from a list of item names into a single flat array.
 * Useful for rendering the "Infos" field in order mode.
 */
export const collectItemChoices = (names: string[]): string[] => {
  return names.flatMap((name) => splitItemChoices(name).choices);
};
